import type { MeContext, PortfolioNode, Role } from '@vizit/api';
import { ROLE } from '../constants';
import { getAccessiblePortfolios } from './get-accessible';
import { hasRole } from './grants';
import { isHierarchical } from './is-hierarchical';
import { isOrgAdmin } from './is-org-admin';

const ROLES_BY_DESCENDING_AUTHORITY: readonly Role[] = [
  ROLE.SuperAdmin,
  ROLE.OrgAdmin,
  ROLE.PortfolioAdmin,
  ROLE.WorkspaceUser,
];

function isPortfolioAdmin(context: MeContext): boolean {
  return hasRole(context, ROLE.PortfolioAdmin);
}

export function hasElevatedGrant(context: MeContext): boolean {
  return isOrgAdmin(context) || isPortfolioAdmin(context);
}

export function canManageUsers(context: MeContext): boolean {
  return hasElevatedGrant(context);
}

export function getGrantableRoles(context: MeContext): Role[] {
  if (isOrgAdmin(context)) {
    return [ROLE.OrgAdmin, ROLE.PortfolioAdmin, ROLE.WorkspaceUser];
  }
  if (isPortfolioAdmin(context)) {
    return [ROLE.PortfolioAdmin, ROLE.WorkspaceUser];
  }
  return [];
}

export function getGrantablePortfolios(context: MeContext): PortfolioNode[] {
  const portfolios = getAccessiblePortfolios(context);
  if (isOrgAdmin(context)) {
    return portfolios;
  }
  const adminPortfolioIds = new Set(
    context.grants.filter((grant) => grant.role === ROLE.PortfolioAdmin).map((grant) => grant.scopeId),
  );
  return portfolios.filter((portfolio) => adminPortfolioIds.has(portfolio.id));
}

export function getPrimaryRole(roles: Role[]): Role | null {
  return ROLES_BY_DESCENDING_AUTHORITY.find((role) => roles.includes(role)) ?? null;
}

function getManagedScopeIds(context: MeContext): Set<string> {
  return new Set(
    getGrantablePortfolios(context).flatMap((portfolio) => [
      portfolio.id,
      ...portfolio.workspaces.map((workspace) => workspace.id),
    ]),
  );
}

export function canManageScope(context: MeContext, scopeId: string | null): boolean {
  if (isOrgAdmin(context)) {
    return true;
  }
  return scopeId !== null && getManagedScopeIds(context).has(scopeId);
}

export function canManageUserWithScopes(context: MeContext, scopeIds: (string | null)[]): boolean {
  if (isOrgAdmin(context)) {
    return true;
  }
  const managed = getManagedScopeIds(context);
  return scopeIds.some((scopeId) => scopeId !== null && managed.has(scopeId));
}

export function canOpenUserManagement(context: MeContext): boolean {
  return !isHierarchical(context.organization) || canManageUsers(context);
}
